// per docs/caiet-uc-spec.md §UC27-02 §UC-ALRT-07 §REUSE-03
// The channel editor as the wireframe draws it (AdminAlerte.tsx:2717-2833): a
// page at its own address, `Identificare` for the name and `Livrare` for the
// kind, its configuration and the destination. The four kinds the delivery
// engine supports stay (in-app, webhook, e-mail, MNotify); the wireframe's
// "Adaptor" has no counterpart in the backend and is not drawn (owner ruling,
// 2026-09-10). The technical name is derived from the name at creation.
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudTextInput, MudSelect } from '@egov-moldova/mud-react';
import { useToast } from '@simsm/ui-shared';
import { type ApiRefusal, apiClient, apiRefusal, loadFailureKind } from '../../app/api-client';
import { FormField, FormPage, FormRow, FormSection } from '../../components/FormPage/FormPage';
import { technicalName } from './AlertRuleFormPage';
import {
  type ChannelDto,
  type ChannelFormState,
  type ChannelKind,
  MNOTIFY_RECIPIENT_KEYS,
  MNOTIFY_RECIPIENT_LABELS,
  configFromDto,
  configToPayload,
  kindLabelKey,
} from './NotificationChannelsPage';

const KINDS: ChannelKind[] = ['inapp', 'webhook', 'email', 'mnotify'];

const EMPTY: ChannelFormState = {
  id: null,
  name: '',
  displayName: '',
  kind: 'inapp',
  config: {},
  active: true,
};

export function NotificationChannelFormPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const isNew = !id;

  const [form, setForm] = useState<ChannelFormState>(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [refusal, setRefusal] = useState<ApiRefusal | null>(null);

  // There is no read-one route for a channel; the register is one short list,
  // read under the same key, so opening the editor from it costs nothing.
  const channels = useQuery({
    queryKey: ['notification-channels'],
    queryFn: () =>
      apiClient
        .get<{ channels: ChannelDto[] }>('/admin/notification-channels')
        .then((r) => r.data.channels),
  });
  const existing = isNew ? undefined : channels.data?.find((channel) => channel.id === id);

  useEffect(() => {
    if (isNew || !existing) return;
    setForm({
      id: existing.id,
      name: existing.name,
      displayName: existing.displayName,
      kind: existing.kind,
      config: configFromDto(existing.config),
      active: existing.active,
    });
  }, [isNew, existing]);

  function edit(patch: Partial<ChannelFormState>): void {
    setForm((current) => ({ ...current, ...patch }));
    setDirty(true);
  }

  function setConfigField(key: string, value: string): void {
    edit({ config: { ...form.config, [key]: value } });
  }

  const save = useMutation({
    mutationFn: (payload: ChannelFormState) =>
      isNew
        ? apiClient
            .post<ChannelDto>('/admin/notification-channels', {
              name: technicalName(payload.displayName, Date.now().toString(36).slice(-4)),
              displayName: payload.displayName.trim(),
              kind: payload.kind,
              config: configToPayload(payload),
              active: payload.active,
            })
            .then((r) => r.data)
        : apiClient
            .patch<ChannelDto>(`/admin/notification-channels/${id}`, {
              displayName: payload.displayName.trim(),
              config: configToPayload(payload),
              active: payload.active,
            })
            .then((r) => r.data),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['notification-channels'] });
      setDirty(false);
      toast({
        variant: 'success',
        title: isNew ? t('alerts.toast.channel.created') : t('alerts.toast.channel.saved'),
        text: saved.displayName,
        closeLabel: t('common.close'),
      });
      navigate('/system-admin/alert-channels');
    },
    onError: (error: unknown) =>
      setRefusal(
        apiRefusal(
          error,
          isNew ? 'alerts.channels.error.create_failed' : 'alerts.channels.error.save_failed',
        ),
      ),
  });

  const displayNameError = form.displayName.trim() ? null : t('form.field.required');

  function handleSave(): void {
    setSubmitted(true);
    setRefusal(null);
    if (displayNameError) return;
    save.mutate(form);
  }

  const textField = (
    key: string,
    labelKey: string,
    options: { hint?: string; secret?: boolean; numeric?: boolean } = {},
  ): JSX.Element => (
    <FormField label={t(labelKey)} {...(options.hint ? { hint: options.hint } : {})}>
      <MudTextInput
        size="md"
        {...(options.secret
          ? { type: 'password' as const, placeholder: t('alerts.channels.form.secret_keep') }
          : {})}
        {...(options.numeric ? { inputMode: 'numeric' as const } : {})}
        value={form.config[key] ?? (key === 'smtp_port' ? '587' : '')}
        onMudInput={(e: CustomEvent<{ value: string }>) => setConfigField(key, e.detail.value)}
      />
    </FormField>
  );

  return (
    <FormPage
      breadcrumb={[
        { label: t('alerts.section.channels'), href: '/system-admin/alert-channels' },
        { label: isNew ? t('alerts.channels.new_crumb') : t('alerts.channels.edit_crumb') },
      ]}
      title={isNew ? t('alerts.channels.new') : t('alerts.channels.edit')}
      lede={t('alerts.channels.form.lede')}
      saveLabel={isNew ? t('alerts.channels.action.create') : t('alerts.action.save_changes')}
      isSaving={save.isPending}
      isDirty={dirty}
      isLoading={channels.isLoading}
      loadError={
        channels.isError
          ? loadFailureKind(channels.error)
          : !isNew && channels.data && !existing
            ? 'not_found'
            : null
      }
      refusal={refusal}
      onSave={handleSave}
      onCancel={() => navigate('/system-admin/alert-channels')}
    >
      <FormSection
        title={t('alerts.section.basics')}
        lede={t('alerts.channels.form.identity_lede')}
      >
        <FormField
          label={t('alerts.channels.field.display_name')}
          required
          hint={t('alerts.channels.field.display_name_hint')}
          error={submitted ? displayNameError : undefined}
        >
          <MudTextInput
            size="md"
            value={form.displayName}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              edit({ displayName: e.detail.value })
            }
          />
        </FormField>
      </FormSection>

      <FormSection
        title={t('alerts.channels.form.delivery')}
        lede={t('alerts.channels.form.delivery_lede')}
      >
        <FormField label={t('alerts.channels.field.kind')} required>
          <MudSelect
            size="medium"
            value={form.kind}
            disabled={!isNew}
            onMudChange={(e: CustomEvent<{ value: string }>) => {
              const kind = e.detail.value as ChannelKind;
              edit({
                kind,
                config:
                  kind === 'mnotify' && !form.config['recipient_label']
                    ? { ...form.config, recipient_label: MNOTIFY_RECIPIENT_LABELS[0] }
                    : form.config,
              });
            }}
          >
            {KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {t(kindLabelKey(kind))}
              </option>
            ))}
          </MudSelect>
        </FormField>

        {form.kind === 'inapp'
          ? textField('route_label', 'alerts.channels.form.route_label', {
              hint: t('alerts.channels.form.route_label_hint'),
            })
          : null}

        {form.kind === 'webhook' ? (
          <>
            {textField('url', 'alerts.channels.form.url')}
            {textField('secret', 'alerts.channels.form.secret', { secret: true })}
          </>
        ) : null}

        {form.kind === 'email' ? (
          <>
            {textField('recipient', 'alerts.channels.form.recipients', {
              hint: t('alerts.channels.form.recipients_hint'),
            })}
            <FormRow columns={2}>
              {textField('smtp_host', 'alerts.channels.form.smtp_host')}
              {textField('smtp_port', 'alerts.channels.form.smtp_port', { numeric: true })}
            </FormRow>
            <FormRow columns={2}>
              {textField('smtp_from', 'alerts.channels.form.smtp_from')}
              {textField('smtp_password', 'alerts.channels.form.smtp_password', { secret: true })}
            </FormRow>
          </>
        ) : null}

        {form.kind === 'mnotify' ? (
          <>
            <FormField
              label={t('alerts.channels.form.recipient')}
              required
              hint={t('alerts.channels.form.recipient_hint')}
            >
              <MudSelect
                size="medium"
                value={form.config['recipient_label'] || MNOTIFY_RECIPIENT_LABELS[0]}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setConfigField('recipient_label', e.detail.value)
                }
              >
                {MNOTIFY_RECIPIENT_LABELS.map((value) => (
                  <option key={value} value={value}>
                    {t(MNOTIFY_RECIPIENT_KEYS[value] ?? value)}
                  </option>
                ))}
              </MudSelect>
            </FormField>
            {textField('api_key', 'alerts.channels.form.api_key', { secret: true })}
          </>
        ) : null}
      </FormSection>
    </FormPage>
  );
}
